import React from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Route-level error boundary. Prevents an uncaught React render error from
 * blanking the whole app. Displays a friendly card with reset + reload actions.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info);
    this.setState({ info });
  }
  reset = () => this.setState({ error: null, info: null });
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-[60vh] grid place-items-center p-8" data-testid="error-boundary">
        <div className="max-w-lg w-full border border-crimson/30 bg-crimson/[0.04] rounded-sm p-6 text-center">
          <div className="h-10 w-10 rounded-sm bg-crimson/15 text-crimson grid place-items-center mx-auto mb-3">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <h2 className="font-display text-xl tracking-tight text-crimson">Something went wrong</h2>
          <p className="text-sm text-muted-foreground mt-2">
            {this.props.label || "This section crashed but the rest of the app is fine."} Your data has not been lost.
          </p>
          <pre className="mt-3 text-[10px] font-mono text-left text-muted-foreground bg-muted/40 rounded-sm p-2 overflow-x-auto max-h-32">
            {String(this.state.error?.message || this.state.error)}
          </pre>
          <div className="flex gap-2 justify-center mt-4">
            <Button variant="outline" className="rounded-sm" onClick={this.reset} data-testid="error-boundary-reset">
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Try again
            </Button>
            <Button className="rounded-sm" onClick={() => window.location.reload()} data-testid="error-boundary-reload">
              Reload page
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
